/**
 * Console diagnostics, tagged by the part of the application they came from.
 *
 * This exists for one reader: somebody with a browser console open, trying to work out
 * why a share failed or why there is no sign-in button. Neither question can be answered
 * from the interface — a peer connection that finds no route and one that was never
 * attempted look identical from the outside, and so do an account service that is down
 * and one that was never configured.
 *
 * Two conventions carry the whole design:
 *
 * - **A tag on every line**, so a console full of unrelated noise can be filtered down to
 *   one subsystem. It is prepended to the message rather than passed as a separate
 *   argument, because the browser's console filter matches text and a separate argument
 *   would not be part of it.
 * - **The level is the volume control.** Milestones — connected, gave up, this is what the
 *   STUN server said — are `info`. Per-attempt detail is `debug`, which browsers hide
 *   unless the reader asks for verbose output. That gives a way to turn the detail on
 *   without inventing a flag of our own, and without a build-time switch that would strip
 *   the lines out of exactly the build a user is complaining about.
 */

/** Which part of the application is speaking. */
export type Tag = 'accounts' | 'share' | 'models' | 'about';

export interface Log {
  /** Per-attempt detail. Hidden by browsers unless verbose output is turned on. */
  debug(message: string, ...detail: unknown[]): void;
  /** A milestone worth seeing without being asked for. */
  info(message: string, ...detail: unknown[]): void;
  /** Something is wrong, but the viewer carries on. */
  warn(message: string, ...detail: unknown[]): void;
  /** Something is wrong and a feature will not work at all. */
  error(message: string, ...detail: unknown[]): void;
}

export function logger(tag: Tag): Log {
  const prefix = `[${tag}]`;

  return {
    debug: (message, ...detail) => {
      console.debug(`${prefix} ${message}`, ...detail);
    },
    info: (message, ...detail) => {
      console.info(`${prefix} ${message}`, ...detail);
    },
    warn: (message, ...detail) => {
      console.warn(`${prefix} ${message}`, ...detail);
    },
    error: (message, ...detail) => {
      console.error(`${prefix} ${message}`, ...detail);
    },
  };
}
